import { Observation } from '@area-monitoring/react-components';
import { BBox, CRS_EPSG4326, S2L1CLayer } from '@sentinel-hub/sentinelhub-js';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContext, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import toBbox from '@turf/bbox';
import { t } from 'ttag';

import { ShAuthContext } from '../../contexts/shAuthContext';
import { ButtonSecondarySmall } from '../Shared/Button/Button';
import { parseFullYearFromScope } from '../../utils/utils';
import { LanguageContext } from '../../contexts/LanguageContext';

const FieldSearchCardWrap = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing04};
  width: 100%;
  .field-card-text-info {
    margin-left: ${({ theme }) => theme.spacing02};
  }
  img {
    object-fit: cover;
    margin-right: ${({ theme }) => theme.spacing01};
    display: block;
  }
  .field-label-wrap {
    :nth-child(1) {
      margin-bottom: ${({ theme }) => theme.spacing01};
    }

    label {
      font-size: ${({ theme }) => theme.fontSize03};
      font-weight: ${({ theme }) => theme.fontWeightMedium};
      margin-right: calc(${({ theme }) => theme.spacing01} / 2);
    }

    .field-value {
      color: ${({ theme }) => theme.colorText50};
      font-size: ${({ theme }) => theme.fontSize03};
      text-transform: capitalize;
    }
  }
  .field-card-buttons-wrap {
    margin-top: ${({ theme }) => theme.spacing02};
    button {
      :nth-child(1) {
        margin-right: ${({ theme }) => theme.spacing01};
      }
    }
  }
  .link-primary-small {
    border: none;
    font-weight: ${({ theme }) => theme.fontWeightMedium};
    cursor: pointer;
    min-height: ${({ theme }) => theme.formElementBaseHeightSmall};
    padding: 0 ${({ theme }) => theme.spacing02};
    border-radius: ${({ theme }) => theme.borderRadius};
    font-size: ${({ theme }) => theme.fontSize03};
    background: ${({ theme }) => theme.colorPrimary50};
    border-radius: ${({ theme }) => theme.borderRadius};
    color: white;
    transition: ${({ theme }) => theme.hoverTransitionSpeed} linear background;
    display: inline-flex;
    align-items: center;
    margin-right: ${({ theme }) => theme.spacing01};
    text-decoration: none;
    margin-bottom: ${({ theme }) => theme.spacing01};
    :hover {
      background: ${({ theme }) => theme.colorPrimary70};
      transition: ${({ theme }) => theme.hoverTransitionSpeed} linear background;
    }
  }
`;

export default function FieldCard({ onClick, onZoomToFieldClick, id, year, geometry, cropType, soilName }) {
  const [cloudfreeDate, setCloudfreeDate] = useState('');
  const { shAuthToken } = useContext(ShAuthContext);
  const [language] = useContext(LanguageContext);
  const theme = useTheme();
  const { search } = useLocation();

  const observationGeometries = useMemo(() => {
    return [
      {
        geometry: geometry,
        style: {
          strokeColor: theme.fieldColor,
          lineWidth: 1,
        },
      },
    ];
  }, [geometry, theme.fieldColor]);

  useEffect(() => {
    async function fetchAvailableDates() {
      const geoJsonToBBox = toBbox(geometry);
      const bbox = new BBox(
        CRS_EPSG4326,
        geoJsonToBBox[0],
        geoJsonToBBox[1],
        geoJsonToBBox[2],
        geoJsonToBBox[3],
      );
      const layer = new S2L1CLayer({
        instanceId: process.env.REACT_APP_SENTINEL2_WMS_ENDPOINT,
        layerId: '1-TRUE-COLOR',
        maxCloudCoverPercent: 0,
      });
      try {
        const availableDates = await layer.findFlyovers(
          bbox,
          new Date(`${parseFullYearFromScope(year)}-01-01`),
          new Date(`${parseFullYearFromScope(year)}-12-31`),
        );

        if (availableDates.length === 0) {
          return;
        }

        setCloudfreeDate(dayjs(availableDates[0].fromTime).format('YYYY-MM-DD'));
      } catch (error) {
        console.error(error);
      }
    }
    fetchAvailableDates();
  }, [cropType, year, geometry]);

  function handleZoomToFieldClick(geometry) {
    const [lng1, lat1, lng2, lat2] = toBbox(geometry);
    const bbox = {
      _southWest: {
        lat: lat1,
        lng: lng1,
      },
      _northEast: {
        lat: lat2,
        lng: lng2,
      },
    };
    onZoomToFieldClick({ bounds: bbox, wasUserDrag: false });
  }

  if (!cloudfreeDate) {
    return <div></div>;
  }

  return (
    <FieldSearchCardWrap onClick={onClick}>
      {shAuthToken && (
        <Observation
          baseUrl={process.env.REACT_APP_SENTINEL2_WMS_ENDPOINT}
          layerId="1-TRUE-COLOR"
          shAuthToken={shAuthToken}
          date={cloudfreeDate}
          width={230}
          language={language}
          height={200}
          geometries={observationGeometries}
          paddingXPercent={50}
          paddingYPercent={50}
          onClick={() => null}
        />
      )}
      <div className="field-card-text-info">
        <div className="field-label-wrap">
          <label htmlFor="">{t`Soil type`}:</label>
          <span className="field-value">{soilName}</span>
        </div>
        <div className="field-label-wrap">
          <label htmlFor="">{t`Crop type`}:</label>
          <span className="field-value">{cropType}</span>
        </div>
        <div className="field-card-buttons-wrap">
          <Link className="link-primary-small" to={`/${id}${search}`}>
            {t`Analyse field`}
          </Link>
          <ButtonSecondarySmall onClick={() => handleZoomToFieldClick(geometry)}>
            {t`Zoom to field`}
          </ButtonSecondarySmall>
        </div>
      </div>
    </FieldSearchCardWrap>
  );
}
